import nsfCheckTemplate from './nsfCheck.template.html';

/*******************************************************************************
 * Component */
/**
 * @ngdoc  component
 * @name   csm.books.nsf.component:Component
 * @description
 * This application provides the ability for a bookkeeper to mark a check as NSF,
 * and grants the ability to mark the items from the sale as not being paid for.
 * They can also return the items within the application, utilizing some of the
 * same components from the Return Receipts application.
 *
 * Since a bank charge can be applied by the bank, the bookkeeper can pass that
 * charge along to the individual that provided the check as payment, by creating
 * a fine of their own.
 *******************************************************************************/
export const nsfCheckComponent = {
  controller: Controller,
  template: nsfCheckTemplate,
  bindings: {
    transcludeAs: '@' // {String} Controller property name in transclusion scope. Default=`$nsfCheck`.
  },
  transclude: {
    remainingAmount: '?remainingAmount',
    bankCharge: '?bankCharge'
  }
};

/*******************************************************************************
 * Controller */
/**
 * Controls the actual data and workflow for the component.
 *******************************************************************************/
/* @ngInject */ function Controller(
  $attrs,
  $element,
  $injector,
  $scope,
  $transclude,
  Watcher
) {
  /** Global services object */
  var $svcs = {
    $attrs,
    $element,
    $ctrl: this,
    $globals: {},
    $scope,
    $injector,
    $transclude,
    Watch: new Watcher($scope)
  };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  $injector.invoke(initGlobals, null, $svcs);

  /*-----------------------*/
  /* Start the application */
  /*-----------------------*/
  var $ctrl = $svcs.$ctrl;
  $ctrl.buildGridOptions();
  $ctrl.getAccounts();
  $ctrl.newNSF();
  $ctrl.checkPreviousData();
}

/*******************************************************************************
 * initCtrl */
/**
 * Initialize the controller objects
 *******************************************************************************/
/* @ngInject */ function initCtrl($ctrl, $injector, $svcs, $transclude) {
  Object.assign($ctrl, {
    fine: { amount: 0 },
    fineCollapsed: true,
    clickSearch: function () {
      document.getElementById('btnSearch').click();
    },
    validators: {},
    filters: {},
    bankChargeAcc: null,
    revenueAcct: null,

    /*-----------------------------*/
    /* Set up injection functions. */
    /*-----------------------------*/
    buildGridOptions: $injector.invoke(buildGridOptions, null, $svcs),
    checkPreviousData: $injector.invoke(checkPreviousData, null, $svcs),
    drop: $injector.invoke(drop, null, $svcs),
    entityLabel: $injector.invoke(entityLabel, null, $svcs),
    getAccounts: $injector.invoke(getAccounts, null, $svcs),
    getEntities: $injector.invoke(getEntities, null, $svcs),
    getEntity: $injector.invoke(getEntity, null, $svcs),
    handleEntity: $injector.invoke(handleEntity, null, $svcs),
    load: $injector.invoke(load, null, $svcs),
    loadCheck: $injector.invoke(loadCheck, null, $svcs),
    loadNSF: $injector.invoke(loadNSF, null, $svcs),
    newNSF: $injector.invoke(newNSF, null, $svcs),
    $onInit: $injector.invoke($onInit, null, $svcs),
    returnItems: $injector.invoke(returnItems, null, $svcs),
    save: $injector.invoke(save, null, $svcs),
    validateEntity: $injector.invoke(validateEntity, null, $svcs)
  });

  /*-----------*/
  /* Accessors */
  /*-----------*/
  Object.defineProperties($ctrl, {
    allowFines: {
      get: function () {
        return $ctrl.GlobalFlags ? $ctrl.GlobalFlags.allowFines : false;
      }
    },
    enableReturns: {
      get: function () {
        return $ctrl.GlobalFlags
          ? $ctrl.nsfCheck.isNew &&
              !!$ctrl.hasReversals &&
              $ctrl.GlobalFlags.canReturn
          : false;
      }
    },
    showReversals: {
      get: function () {
        return $ctrl.GlobalFlags
          ? $ctrl.hasReversals && $ctrl.GlobalFlags.allowReversals
          : false;
      }
    },
    showOnAccount: {
      get: function () {
        return $ctrl.GlobalFlags ? $ctrl.GlobalFlags.allowFunds : false;
      }
    },
    acctLabel: {
      get: function () {
        return $ctrl.GlobalFlags.accountLabel;
      }
    },
    hasReversals: {
      get: function () {
        return $ctrl.nsfCheck.checkPayment.reversals.length;
      }
    },
    notRecorded: {
      get: function () {
        return !$ctrl.nsfCheck.checkPayment.id;
      }
    },
    remainingAmountTranscluded: {
      get: function () {
        return $transclude.isSlotFilled('remainingAmount');
      }
    },
    bankChargeTranscluded: {
      get: function () {
        return $transclude.isSlotFilled('bankCharge');
      }
    },
    revenueAcctID: {
      get: function () {
        return $ctrl.nsfCheck.revenue.accountID;
      },
      set: function (accountID) {
        $ctrl.nsfCheck.revenue.accountID = accountID;

        $ctrl.revenueAcct =
          !$ctrl.distAccounts || !$ctrl.nsfCheck.revenue.accountID
            ? null
            : $ctrl.distAccounts
                .filter(function (acct) {
                  return (
                    acct.accountID == ($ctrl.nsfCheck.revenue.accountID || 0)
                  );
                })
                .shift();
      }
    },
    bankChargeAcctID: {
      get: function () {
        return $ctrl.nsfCheck.bankCharge.accountID;
      },
      set: function (accountID) {
        $ctrl.nsfCheck.bankCharge.accountID = accountID;
        $ctrl.bankChargeAcct =
          !$ctrl.distAccounts || !$ctrl.nsfCheck.bankCharge.accountID
            ? null
            : $ctrl.chargeAccounts
                .filter(function (acct) {
                  return (
                    acct.accountID == ($ctrl.nsfCheck.bankCharge.accountID || 0)
                  );
                })
                .shift();
      }
    },

    availableFunds: {
      get: function () {
        /*------------------------------------------------*/
        /* If this was from a receipt that had a          */
        /* receiptFrom, get the funds for the individual. */
        /*------------------------------------------------*/
        if ($ctrl.nsfCheck.checkPayment.hasEntity)
          return $ctrl.nsfCheck.checkPayment.receiptFrom.entity.funds;
        return 0;
      }
    },

    attachedEntity: {
      get: function () {
        /**
         * If there is an existing entity attached to the check, we want to make sure
         * we are displaying them on the "Individuals" input field. In order to format
         * the input field to look like "doe, jane", it requires the existing value to
         * pass in a entity object with a id object assigned.
         * Ex: { entity: { id: 12345 } }
         * We look through these specific values in this order of priority:
         *  receiptFrom -> This is the value we get from the "From" field in Write New Receipt page.
         *              -> It returns back an object: { id: 12345, name: '' }
         *  studentFunds.entityID -> Is the value that we set when saving a NSF Check.
         *                        -> It returns back a number so we return it back in the correct format.
         *  checkPayment.reversals -> We grab the first reversal object with an entityID, and return the entityID.
         *                         -> It returns back a number so we return it back in the correct format.
         * */
        if ($ctrl.nsfCheck.checkPayment.receiptFrom)
          return $ctrl.nsfCheck.checkPayment.receiptFrom;
        if ($ctrl.nsfCheck.studentFunds.entityID)
          return { entity: { id: $ctrl.nsfCheck.studentFunds.entityID } };

        /**
         * Reversals do not always exist (on load, or for checks that do not use reversals)
         * so we must add in extra nullish Coalesce checks to allow for these situations to prevent erroring.
         **/
        const allReversals = $ctrl.nsfCheck.reversals;
        const matchedReversal = Array.isArray(allReversals)
          ? allReversals.find(function (reversal) {
              return reversal.entityID;
            })
          : null;

        const reversalEntityID = matchedReversal
          ? matchedReversal.entityID
          : null;

        if (reversalEntityID) return { entity: { id: reversalEntityID } };
      },
      set: function (entity) {
        $ctrl.nsfCheck.checkPayment.receiptFrom = entity;
      }
    }
  });
}

/*******************************************************************************
 * initGlobals */
/**
 * Initialize the global objects
 *******************************************************************************/
/* @ngInject */ function initGlobals($globals) {
  Object.assign($globals, {
    entityMakerActive: false
  });
}

/*******************************************************************************
 * buildGridOptions */
/**
 * Builds the grid options.
 *******************************************************************************/
/* @ngInject */ function buildGridOptions(
  $ctrl,
  CommonUIGridTemplates,
  uiGridConstants
) {
  return function () {
    /*--------------*/
    /* Grid options */
    /*--------------*/
    $ctrl.itemsGrid = {
      enableCellEdit: false,
      enableCellEditOnFocus: true,
      enableColumnMenus: false,
      enableFiltering: false,
      enableRowSelection: false,
      enableFullRowSelection: false,
      enableRowHeaderSelection: false,
      multiSelect: false,
      enableSelectAll: false,
      saveFilter: false,
      saveSort: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      onRegisterApi: function (gridApi) {
        $ctrl.itemsGrid.api = gridApi;
      },
      minRowsToShow: 5,
      cellEditableCondition: function () {
        return $ctrl.nsfCheck.isNew;
      },
      data: [],
      columnDefs: [
        {
          displayName: 'Description',
          field: 'memo'
        },
        {
          field: 'amount',
          cellClass: 'text-right',
          cellFilter: 'currency',
          width: '15%'
        },
        {
          field: 'reversal',
          cellClass: 'text-right',
          cellFilter: 'currency',
          precision: 2,
          editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
          enableCellEdit: true,
          width: '15%'
        }
      ]
    };
  };
}

/*******************************************************************************
 * checkPreviousData */
/**
 * If we are coming back from handling returns, we should have saved NSF
 * information. Reload the check payment, and set the still relevant data.
 *******************************************************************************/
/* @ngInject */ function checkPreviousData($ctrl, Data) {
  return function () {
    if (Data.nsfCheck) {
      $ctrl.loadCheck(Data.nsfCheck.checkPayment.id).then(function () {
        /*-------------------------------*/
        /* Reconcile the item reversals. */
        /*-------------------------------*/
        $ctrl.nsfCheck.checkPayment.reversals.forEach(function (nsfReversal) {
          Data.nsfCheck.checkPayment.reversals.some(function (dataReversal) {
            /*--------------------------------------------------------*/
            /* If any quantity of item remains unreturned, we need to */
            /* update the reversal amount to whatever is left over.   */
            /*--------------------------------------------------------*/
            if (nsfReversal.ledgerItemID == dataReversal.ledgerItemID) {
              nsfReversal.reversal = Math.min(
                nsfReversal.amount,
                dataReversal.reversal
              );
              return true;
            }
          });
        });

        /*----------------------------------------------------*/
        /* Making changes to the nsfCheck object will trigger */
        /* the Watcher. We want this to happen as these are   */
        /* still going to be seen as unsaved changes.         */
        /*----------------------------------------------------*/
        $ctrl.nsfCheck.entryDate = Data.nsfCheck.entryDate;
        $ctrl.nsfCheck.fine = Data.nsfCheck.fine;
        $ctrl.nsfCheck.revenue = Data.nsfCheck.revenue;
        $ctrl.nsfCheck.bankCharge = Data.nsfCheck.bankCharge;

        Data.nsfCheck = null;
      });
    }
  };
}

/*******************************************************************************
 * drop */
/**
 * Drops the NSF. Does nothing if this has not yet been saved.
 *******************************************************************************/
/* @ngInject */ function drop($ctrl, Alert, BusyScreen, NSFChecksService) {
  return function () {
    if (!$ctrl.nsfCheck.id) return;

    Alert.confirm(
      'Are you sure you want to remove this NSF?',
      'Confirm Drop'
    ).then(function (result) {
      /*-------------------------------------*/
      /* Drops the NSF and starts a new one. */
      /*-------------------------------------*/
      BusyScreen.promise(
        NSFChecksService.drop($ctrl.nsfCheck.id).then(
          function () {
            Alert.info('The NSF has been dropped', 'NSF Dropped');
            $ctrl.newNSF();
          },
          /*----------------*/
          /* Error handler. */
          /*----------------*/
          function (resp) {
            Alert.error(resp.parsedResponse.message(), 'Save Error');
          }
        )
      );
    });
  };
}

/*******************************************************************************
 * entityLabel */
/**
 * Returns a string version of the entity. If this is a student, the studentID
 * will be included in the label.
 *
 * @param  $data  Selected entity data.
 *******************************************************************************/
/* @ngInject */ function entityLabel($ctrl) {
  return function ($data) {
    if ($data && $data.entity)
      return '{0}{1}'.format(
        $data.entity.name,
        $data.entity.studentID ? ' ({0})'.format($data.entity.studentID) : ''
      );

    return null;
  };
}

/*******************************************************************************
 * getAccounts */
/**
 * Collects the accounts for dataproviders.
 *******************************************************************************/
/* @ngInject */ function getAccounts($ctrl, BusyScreen, NSFChecksService) {
  return function () {
    BusyScreen.promise(
      NSFChecksService.getAccounts().then(function (accounts) {
        $ctrl.bankAccounts = accounts.bankAccounts;
        $ctrl.chargeAccounts = accounts.expenses;
        $ctrl.distAccounts = accounts.revenue;
        $ctrl.fundsAccount = accounts.funds;
      })
    );
  };
}

/*******************************************************************************
 * getEntities */
/**
 * Collects a list of entities for use in the Receipt From typeahead
 * dataprovider.
 *
 * @param  text  Text filter for the entities.
 *******************************************************************************/
/* @ngInject */ function getEntities($ctrl, $q, BooksSvc) {
  return function (text) {
    /*---------------------------------------*/
    /* No input passed, return empty promise */
    /*---------------------------------------*/
    if (!text) return $q.resolve(null);

    var defer = $q.defer();
    BooksSvc.findAddressEntities(text, null, true).then(function (resp) {
      var entities = resp.data.sort(function (a, b) {
        return a.entity.fullName
          .toLowerCase()
          .localeCompare(b.entity.fullName.toLowerCase());
      });
      defer.resolve(entities);
    });
    return defer.promise;
  };
}

/*******************************************************************************
 * getEntity */
/**
 * Retrieves full detail for an entity when loaded from a recorded NSF.
 *
 * @param  id  Entity ID of the recorded NSF.
 *******************************************************************************/
/* @ngInject */ function getEntity($ctrl, $q, $timeout, BooksSvc, Watch) {
  return function (id) {
    /*---------------------------------------*/
    /* No input passed, return empty promise */
    /*---------------------------------------*/
    if (!id) return $q.resolve(null);

    var defer = $q.defer();

    Watch.pause();
    BooksSvc.getAddressEntityListItem(id).then(function (resp) {
      defer.resolve(resp.data);
      $timeout(Watch.unpause, 50);
    });

    return defer.promise;
  };
}

/*******************************************************************************
 * handleEntity */
/**
 * For the "Individual" input field, if the typed in user does not exist It will
 * ask the user if they want to add the entity to our system. If not the user
 * will remain within the input field and whenever they click off the prompt to
 * create a user will continueously be asked until a valid entity is either
 * created or an existing entity is entered.
 *
 * @param  $event  Event object
 *******************************************************************************/
/* @ngInject */ function handleEntity(
  $globals,
  $ctrl,
  $scope,
  BooksSvc,
  BusyScreen,
  EntityMaker,
  SERVER,
  Alert
) {
  return function ($event) {
    /** standard initialization for Lazy type. */
    var ltID = $event.target.parentElement.id;
    var ltElem = angular.element(document.getElementById(ltID));
    var modelProp = ltElem.attr('ng-model');
    var addressEntity = $scope.$eval(modelProp);
    var restrictAllEntities =
      !!SERVER.appFlags.restrictVendors &&
      !!SERVER.appFlags.restrictIndividuals;

    /*-------------------------------------------------------------*/
    /* Clear out the entity values if this isn't an entity object. */
    /*-------------------------------------------------------------*/
    if (!addressEntity) {
      $ctrl.nsfCheck.checkPayment.receiptFrom = null;

      return;
    }

    /*---------------------------------------------------------*/
    /* If the received value is an object we can assume        */
    /* that it is an existing entity and set that entity info  */
    /* as the current input fields value, otherwise we run     */
    /* the else statement to create a new entity.              */
    /*---------------------------------------------------------*/
    if (angular.isObject(addressEntity)) {
      $ctrl.nsfCheck.checkPayment.receiptFrom = addressEntity;
    } else if (!restrictAllEntities) {
      /*---------------------------------------------------------*/
      /* Show EntityMaker Modal.                                 */
      /* This will offer the user the option to create the       */
      /* unrecognized entity within our system as a new entity   */
      /*---------------------------------------------------------*/
      $globals.entityMakerActive = true;

      /*-------------------------------------*/
      /* Build a new entity object with the  */
      /* result from EntityMaker and use it. */
      /*-------------------------------------*/
      EntityMaker.newEntity(addressEntity)
        .then(
          function (result) {
            BusyScreen.setBusy(true);

            if (result.added)
              return BooksSvc.getAddressEntityListItem(result.entity.id).then(
                function (resp) {
                  $ctrl.nsfCheck.checkPayment.receiptFrom = resp.data;
                }
              );
          },

          /*----------------------------------------------------------------*/
          /* User declined / cancelled the create-entity prompt. Keep focus  */
          /* on the field (intentionally locking the user in) until they     */
          /* select a valid entity, create a new one, or clear the field —   */
          /* leaving unrecognized free text is not allowed.                  */
          /*----------------------------------------------------------------*/
          function () {
            $event.target.focus();
          }
        )
        .finally(function () {
          BusyScreen.setBusy(false);
          $globals.entityMakerActive = false;
          $event.target.focus();
        });
    } else {
      $event.target.value = '';
      Alert.info('You must select an existing entity.', 'NSF Check');
    }
  };
}

/*******************************************************************************
 * load */
/**
 * Attempts to load an NSF. This is used by the find dialog as well as the Next
 * and Previous buttons. When coming from the dialog, the direction (`dir`) will
 * be zero, and we will get the information for the specific `id` provided. Next
 * and Previous will use the direction parameter (1 and -1 respectively) to
 * determine whether to search for a higher or lower NSF from the `id` provided.
 *
 * @param  id   Current NSF ID
 * @param  dir  Direction to search for. -1=Previous, 0=Exact, 1=Next
 *******************************************************************************/
/* @ngInject */ function load(
  $ctrl,
  Alert,
  BusyScreen,
  Data,
  NSFChecksService,
  Watch
) {
  return function (id, dir) {
    Watch.checkForChanges(function () {
      BusyScreen.promise(
        NSFChecksService.getNSF(id || 0, dir).then(function (data) {
          /*----------------------------------------------------------------*/
          /* If we were trying to go forward or backward, but the server    */
          /* returned an NSF with no ID, then there are no more NSF         */
          /* Checks. If we were going backward, remain on the current NSF   */
          /* and alert the user. If we were going forward, start a new NSF. */
          /*----------------------------------------------------------------*/
          if (!data.nsfCheck.id)
            if (dir < 0) {
              Alert.info('There are no more NSFs', 'No More NSFs');
              return;
            } else if (dir > 0) {
              $ctrl.newNSF();
              return;
            }

          /*----------------------------------------------------------*/
          /* A NSF was found, send the data to the new creation step. */
          /*----------------------------------------------------------*/
          $ctrl.loadNSF(new Data.NSFCheck(data.nsfCheck, data.checkPayment));
        })
      );
    });
  };
}

/*******************************************************************************
 * loadCheck */
/**
 * Loads the check payment details. This will also load in some information from
 * the receipt such as the receiptFrom, the memo, and the items.
 *
 * @param  id  Check payment ID.
 *******************************************************************************/
/* @ngInject */ function loadCheck($ctrl, BusyScreen, Data, NSFChecksService) {
  return function (id) {
    BusyScreen.setBusy(true);
    return NSFChecksService.getCheck(id, true, false).then(function (data) {
      BusyScreen.setBusy(false);

      $ctrl.loadNSF(new Data.NSFCheck(null, data, true));
    });
  };
}

/*******************************************************************************
 * loadNSF */
/**
 * Loads the NSF information into the controller object to be used in the display.
 *
 * @param  nsfCheck  NSF Check object to load into the controller.
 *******************************************************************************/
/* @ngInject */ function loadNSF($ctrl, Watch) {
  return function (nsfCheck) {
    Watch.deregister();
    $ctrl.nsfCheck = nsfCheck;
    $ctrl.itemsGrid.data = $ctrl.nsfCheck.checkPayment.reversals.map(
      function (cpr) {
        if ($ctrl.nsfCheck.reversals)
          $ctrl.nsfCheck.reversals.forEach(function (r) {
            if (r.referenceItemID == cpr.ledgerItemID)
              return (cpr.mReversal = r.amount);
          });

        return cpr;
      }
    );
    $ctrl.filters.checkName = $ctrl.nsfCheck.checkPayment.checkName;
    $ctrl.filters.checkNumber = $ctrl.nsfCheck.checkPayment.checkNumber;
    $ctrl.fineCollapsed = true;

    /*-------------------------------------*/
    /* We need to show the original amount */
    /* column when working on a new NSF.   */
    /*-------------------------------------*/
    $ctrl.itemsGrid.columnDefs[1].visible = $ctrl.nsfCheck.isNew;
    Watch.register($ctrl.nsfCheck);
  };
}

/*******************************************************************************
 * newNSF */
/**
 * Clear input fields and start a new NSF object.
 *******************************************************************************/
/* @ngInject */ function newNSF($ctrl, Data, FineLevyService, Watch) {
  return function () {
    Watch.checkForChanges(function () {
      $ctrl.itemsGrid.data = [];
      $ctrl.filters = {
        checkName: null,
        checkNumber: null
      };

      /*------------------------------------------------------------*/
      /* Clear the fine component inputs using the FineLevyService. */
      /*------------------------------------------------------------*/
      FineLevyService.clear();

      $ctrl.loadNSF(new Data.NSFCheck(null, null));
    });
  };
}

/*******************************************************************************
 * Controller.$onInit */
/**
 * Sets properties when component has initialized.
 *******************************************************************************/
/* @ngInject */ function $onInit(
  $attrs,
  $ctrl,
  $element,
  $timeout,
  $transclude,
  CompilerService,
  SERVER
) {
  return function () {
    var values = {};
    if ($attrs.transcludeAs) values[$attrs.transcludeAs] = $ctrl;

    $timeout(function () {
      CompilerService.transcludeSlots($element, values, $transclude);
    }, 50);

    $ctrl.GlobalFlags = {
      canReturn: SERVER.BooksWebConstants.allowReturns,
      accountLabel: SERVER.BooksWebConstants.accountLabel,
      allowFines: SERVER.BooksWebConstants.allowFines,
      allowFunds: SERVER.BooksWebConstants.allowFunds,
      allowReversals: SERVER.BooksWebConstants.allowReversals,
      showAcctNumber: SERVER.BooksWebConstants.showAcctNumber
    };
  };
}

/*******************************************************************************
 * returnItems */
/**
 * Save the current NSF information and send the application to the returns
 * component for the receipt ID.
 *******************************************************************************/
/* @ngInject */ function returnItems($ctrl, $location, Data) {
  return function () {
    Data.nsfCheck = $ctrl.nsfCheck;
    $location.path('returnItems/' + $ctrl.nsfCheck.checkPayment.receiptID);
  };
}

/*******************************************************************************
 * save */
/**
 * Validate the required input fields and build the actual NSF object to save to
 * the server.
 *******************************************************************************/
/* @ngInject */ function save(
  $ctrl,
  Alert,
  BusyScreen,
  Data,
  NSFChecksService,
  Tools,
  Watch
) {
  return function () {
    /*------------------*/
    /* Validate fields. */
    /*------------------*/
    $ctrl.validators.reset();
    if (!$ctrl.validators.validate()) return;

    var entityID = $ctrl.nsfCheck.checkPayment.hasEntity
      ? $ctrl.nsfCheck.checkPayment.receiptFrom.entity.id
      : 0;

    /*------------------------------------------*/
    /* Create the items and save the NSF Check. */
    /*------------------------------------------*/
    var nsfCheck = $ctrl.nsfCheck.toJSON();
    nsfCheck.revenue.entityID = entityID; //todo look back here

    /*--------------------*/
    /* Add the fine item. */
    /*--------------------*/
    if ($ctrl.fine && $ctrl.fine.amount) nsfCheck.fineItem = $ctrl.fine;

    /*---------------------------------------------------------------------*/
    /* If there are any revenue reversals against the items, we need to    */
    /* create line items for each of them with a positive reversal amount. */
    /*---------------------------------------------------------------------*/
    nsfCheck.reversals = nsfCheck.checkPayment.reversals.filter(
      function (reversal) {
        /*--------------------------------------*/
        /* Reset the original amount as this    */
        /* is the field used by the LedgerItem. */
        /*--------------------------------------*/
        reversal.amount = reversal.reversal;

        /*-----------------------------------------------------------------*/
        /* We need to set the referenceItemID to that of the original      */
        /* item to make sure those items will be marked as unpaid.         */
        /* In the cases where the original receipt never has a             */
        /* receiptFrom value, the reversals entityID may not exist         */
        /* which can cause problems when trying to save a reversal.        */
        /* To prevent the save error from happening, we check everytime    */
        /* a reversal is being made to see if it has an entityID attached  */
        /* if not we use the entityID we get from the entity entered       */
        /* in the "Individual" field.                                      */
        /*-----------------------------------------------------------------*/
        if (reversal.amount > 0) {
          reversal.entityID = reversal.entityID || entityID;
          return (reversal.referenceItemID = reversal.ledgerItemID);
        }

        return false;
      }
    );

    /*-----------------------------------------------------------------------*/
    /* Create the checkItem object. This is the line that will credit the    */
    /* bank account for the amount of the check and any applied bank charge. */
    /*-----------------------------------------------------------------------*/
    nsfCheck.checkItem = {
      accountID: nsfCheck.checkPayment.bankAcctID,
      amount: Tools.roundMoney(
        nsfCheck.checkPayment.checkAmount + nsfCheck.chargeAmount
      ),
      referenceItemID: nsfCheck.checkPayment.id || 0
    };

    /*-----------------------------------------*/
    /* Set the account ID for the student      */
    /* funds if there is any money being used. */
    /*-----------------------------------------*/
    if (nsfCheck.studentFunds.amount) {
      nsfCheck.studentFunds.accountID = $ctrl.fundsAccount;
      nsfCheck.studentFunds.entityID = entityID;
    }

    BusyScreen.setBusy(true);
    NSFChecksService.save(nsfCheck).then(
      function () {
        BusyScreen.setBusy(false);
        Alert.success('The NSF has been recorded');
        Watch.deregister();
        $ctrl.newNSF();
      },
      function (err) {
        BusyScreen.setBusy(false);
        Alert.error(err.parsedResponse.message(), 'Save Error');
      }
    );
  };
}

/*******************************************************************************
 * validateEntity */
/**
 * Custom validation for the entity.
 *******************************************************************************/
/* @ngInject */ function validateEntity($ctrl) {
  return function () {
    /**
     * These are the cases we want to enforce the users to enter in the "Individual" input field.
     * If the NSF Check has any of the two values but no entityID:
     *  "On Account" amount
     *  "Revenue Reversed" amount -> This will mainly be a negative value
     *  "No EntityID exists"
     * */
    if (
      ($ctrl.nsfCheck.onAccount || $ctrl.nsfCheck.checkPayment.totalReversed) &&
      !$ctrl.nsfCheck.checkPayment.entityID
    )
      return 'Value is required';

    return null;
  };
}
